import { AHSeparator, Box } from "arkh";
import React, { useCallback, useMemo } from "react";
import { BaseWidgetProps } from "../BaseProps";
import { graphql, useFragment } from "react-relay";
import { useSetWidgetFocus } from "../useSetFocus";
import { ListRenderItem } from "react-native";
import { WidgetFilePage, WidgetFilePageProcessing } from "./WidgetFilePage";
import { WidgetFileDynamic_widgetFile$key } from "./__generated__/WidgetFileDynamic_widgetFile.graphql";
import Animated, {
  runOnJS,
  useAnimatedReaction,
  useAnimatedScrollHandler,
  useDerivedValue,
  useSharedValue,
} from "react-native-reanimated";
import { useWidgetLayout } from "../WidgetLayoutContext";
import { PageIndicatorAnimated } from "./PageIndicatorAnimated";

export const WidgetFileDynamic = React.memo(WidgetFileDynamicC);
function WidgetFileDynamicC(
  props: {
    widget: WidgetFileDynamic_widgetFile$key;
    pageId?: string;
    onPagePress: (fileId: string, pageId: string) => void;
  } & BaseWidgetProps
) {
  const widget = useFragment(
    graphql`
      fragment WidgetFileDynamic_widgetFile on WidgetFile {
        ...WidgetFileStatic_widgetFile
        id
        title
        files {
          pages {
            id
            ...WidgetFilePage_page
          }
          file {
            id
            status
            ...WidgetFilePageProcessing_file
            name
          }
        }
      }
    `,
    props.widget
  );

  const focus = useSetWidgetFocus(widget.id, props.storecastId);

  type ListItemType =
    | {
        fileId: string;
        type: "PAGE";
        page: (typeof widget.files)[number]["pages"][number];
      }
    | {
        fileId: string;
        type: "PROCESSING";
        file: (typeof widget.files)[number]["file"];
      };

  const listData = useMemo(
    () =>
      widget.files.flatMap<ListItemType>((f) =>
        f.file.status === "READY"
          ? f.pages.map((p) => ({
              fileId: f.file.id,
              page: p,
              type: "PAGE" as const,
            }))
          : [{ type: "PROCESSING" as const, file: f.file, fileId: f.file.id }]
      ),
    [widget]
  );

  const y = useSharedValue(0);
  const scrollHandler = useAnimatedScrollHandler({
    onScroll: (e) => {
      y.value = e.contentOffset.y;
    },
  });

  const { height } = useWidgetLayout();

  const currentIndex = useDerivedValue(
    () => Math.max(0, Math.round(y.value / height)),
    [y, height]
  );
  const focusIndex = useCallback(
    (index: number) => {
      const item = listData[index];
      if (item && item.type === "PAGE") {
        focus(item.page.id);
      }
    },
    [focus, listData]
  );

  const activeFocus = props.mode === "FOCUS" && props.isFocused;
  useAnimatedReaction(
    () => currentIndex.value,
    (current, prev) => {
      if (activeFocus && current !== prev) {
        runOnJS(focusIndex)(current);
      }
    }
  );

  const { onEvent } = props;

  const renderItem: ListRenderItem<(typeof listData)[number]> = useCallback(
    ({ item }) => {
      return item.type === "PAGE" ? (
        <WidgetFilePage
          fileId={item.fileId}
          onPress={props.onPagePress}
          page={item.page}
        />
      ) : (
        <WidgetFilePageProcessing file={item.file} />
      );
    },
    [props.onPagePress]
  );

  return (
    <>
      <Box
        color="surface-backgroundControl"
        cornerRadius="medium"
        style={{ flex: 1, overflow: "hidden" }}
      >
        <Animated.FlatList
          onScroll={scrollHandler}
          ItemSeparatorComponent={AHSeparator}
          data={listData}
          renderItem={renderItem}
          pagingEnabled
          showsVerticalScrollIndicator={false}
          keyExtractor={(item) =>
            item.type === "PAGE" ? item.page.id : item.file.id
          }
        />
      </Box>
      {listData.length > 1 ? (
        <PageIndicatorAnimated
          currentPage={currentIndex}
          numberOfItems={listData.length}
        />
      ) : null}
    </>
  );
}
